import { getRichTextExcerpt, getRichTextPlainText } from './richText';
import { buildProductPath } from './productRoutes';

const DEFAULT_PRODUCT_CATEGORIES = [
  'Tube Fittings',
  'Valves',
  'Double Block & Bleed Valves',
  'Manifolds',
  'Flange Adaptors',
  'Regulators',
  'Filters',
  'Swivel Gauge Adaptor',
  'Air Manifolds',
  'Condensate Pot',
  'Sampling System',
  'Pressure Gauge',
  'Temperature Gauge',
  'Thermowells',
  'Accessories',
  'Others',
];

const DEFAULT_MISSION_SHORT =
  'HKI stands as a leading manufacturer of precision engineering products, serving as the pivotal link between quality and innovation.';
const DEFAULT_MISSION_FULL =
  'The company mission is to make precision engineering accessible and reliable for businesses of every scale.';
const DEFAULT_VISION_SHORT =
  'To be a global leader in precision engineering products like fittings, valves, flanges, regulators, gauges, seals, and custom-engineered products.';
const DEFAULT_VISION_FULL =
  'We aspire to expand worldwide while fostering a human-centric company culture that empowers employees.';
const DEFAULT_WHY_CHOOSE_TITLE = 'Why Industries Choose HKI for';
const DEFAULT_WHY_CHOOSE_HIGHLIGHT = 'Superior Performance';
const DEFAULT_WHY_CHOOSE_INTRO =
  'Industries choose HKI for unmatched quality, innovative engineering, and reliable performance.';
const DEFAULT_WHY_CHOOSE_ITEMS = [
  {
    title: 'Authorized Supplier',
    description:
      'Providing genuine industrial valves, fittings, and instrumentation for trusted performance.',
  },
  {
    title: 'Expert Guidance',
    description: 'Helping customers find the perfect solutions for operational requirements.',
  },
  {
    title: 'Reliable Support',
    description: 'Assisting from product selection to after-sales service.',
  },
];
const DEFAULT_INDUSTRIES_TITLE = 'Industries We Serve';
const DEFAULT_INDUSTRIES_INTRO =
  'HKI serves manufacturing, energy, oil and gas, chemical, water treatment, pharmaceutical, power, steel, paper, and sugar industries.';
const DEFAULT_INDUSTRIES = [
  'Oil & Gas',
  'Chemical Industry',
  'Water & Waste Water',
  'Power & Energy',
  'Cement Plants',
  'Metallurgical & Steel',
  'Pharmaceutical',
  'Paper & Sugar Plants',
];
const DEFAULT_ABOUT_PARAGRAPHS = [
  'HKI stands as a beacon of excellence in the precision engineering industry with more than 29 years of experience.',
  'The company serves clients across 50 plus countries with industrial valves, fittings, and precision components.',
];
const DEFAULT_ABOUT_WHY = [
  'Expertise & Experience',
  'Quality Assurance',
  'Global Reach',
];
const DEFAULT_CONTACT_PHONE = '+91 9167633933';
const DEFAULT_CONTACT_EMAIL = 'sales@hkinds.com';
const DEFAULT_CONTACT_ADDRESS =
  'A-1001, Viceroy Park, Opp RTO office, Kandarpada, Dahisar-West, Mumbai-400068';

const SEARCH_RESULT_TYPE_ORDER = {
  Product: 0,
  News: 1,
  Page: 2,
};
const MEDIA_REFERENCE_PATTERN = /^(https?:\/\/|data:|\/)/i;
const MEDIA_EXTENSION_PATTERN = /\.(svg|png|jpe?g|webp|gif|pdf)(?:$|\?)/i;

const FIELD_WEIGHTS = {
  title: { exact: 260, startsWith: 210, includes: 185, tokens: 18 },
  pageTitle: { exact: 200, startsWith: 165, includes: 140, tokens: 12 },
  section: { exact: 180, startsWith: 145, includes: 125, tokens: 10 },
  keywords: { exact: 150, startsWith: 125, includes: 105, tokens: 8 },
  content: { exact: 120, startsWith: 95, includes: 85, tokens: 6 },
};

const MIN_PREFIX_MATCH_LENGTH = 3;

function uniqueValues(values = []) {
  return Array.from(new Set(values.filter(Boolean)));
}

function flattenTextValues(value, bucket = []) {
  if (value == null) {
    return bucket;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const rawValue = String(value).trim();

    if (MEDIA_REFERENCE_PATTERN.test(rawValue) || MEDIA_EXTENSION_PATTERN.test(rawValue)) {
      return bucket;
    }

    const text = getPlainSearchText(rawValue);

    if (text) {
      bucket.push(text);
    }

    return bucket;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => flattenTextValues(item, bucket));
    return bucket;
  }

  if (typeof value === 'object') {
    Object.values(value).forEach((item) => flattenTextValues(item, bucket));
  }

  return bucket;
}

function normalizeSubcategorySource(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || '').trim()).filter(Boolean);
      }
    } catch (_) {}

    return value
      .split(/\r\n|\r|\n|,/)
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }

  return [];
}

function collectSiteContentByKey(siteContent, matchers = []) {
  if (!siteContent || typeof siteContent !== 'object') {
    return [];
  }

  return Object.entries(siteContent).flatMap(([key, value]) => {
    const isMatch = matchers.some((matcher) => {
      if (matcher instanceof RegExp) {
        return matcher.test(key);
      }

      return key.startsWith(matcher) || key.includes(matcher);
    });

    return isMatch ? flattenTextValues(value) : [];
  });
}

function buildCategoryRecords(siteContent) {
  const categoryMap = new Map();

  const registerCategory = (label, section) => {
    const cleanLabel = String(label || '').trim();
    const normalizedLabel = normalizeSearchValue(cleanLabel);

    if (!cleanLabel || !normalizedLabel || categoryMap.has(normalizedLabel)) {
      return;
    }

    categoryMap.set(normalizedLabel, {
      label: cleanLabel,
      section,
    });
  };

  const dropdownItems = Array.isArray(siteContent?.product_dropdown_items)
    ? siteContent.product_dropdown_items
    : [];

  dropdownItems.forEach((item) => registerCategory(item, 'Category'));

  const categorySliderItems = Array.isArray(siteContent?.product_category_slider)
    ? siteContent.product_category_slider
    : [];

  categorySliderItems.forEach((item) => {
    registerCategory(item?.displayName || item?.name || item?.key || '', 'Category');
    normalizeSubcategorySource(item?.subcategories).forEach((subcategory) =>
      registerCategory(subcategory, 'Subcategory')
    );
  });

  const footerProducts = Array.isArray(siteContent?.footer_products)
    ? siteContent.footer_products
    : [];

  footerProducts.forEach((item) => {
    registerCategory(item?.label || '', 'Category');
    registerCategory(item?.category || '', 'Category');
  });

  DEFAULT_PRODUCT_CATEGORIES.forEach((item) => registerCategory(item, 'Category'));

  return Array.from(categoryMap.values());
}

function buildCategoryTerms(siteContent) {
  return buildCategoryRecords(siteContent).map((item) => item.label);
}

function normalizeProductFeatures(value) {
  let features = value;

  if (typeof features === 'string') {
    try {
      const parsed = JSON.parse(features);
      features = Array.isArray(parsed) ? parsed : [features];
    } catch (_) {
      features = features.split('\n');
    }
  }

  if (!Array.isArray(features)) {
    return [];
  }

  return features.map((feature) => getPlainSearchText(feature)).filter(Boolean);
}

function normalizeProductSpecifications(value) {
  let specifications = value;

  if (!specifications) {
    return [];
  }

  if (typeof specifications === 'string') {
    try {
      const parsed = JSON.parse(specifications);
      specifications = Array.isArray(parsed)
        ? parsed
        : [{ name: 'Description', value: specifications }];
    } catch (_) {
      specifications = [{ name: 'Description', value: specifications }];
    }
  }

  if (!Array.isArray(specifications)) {
    return [];
  }

  return specifications.flatMap((specification) => {
    if (!specification || typeof specification !== 'object') {
      return [];
    }

    const label = getPlainSearchText(specification.name || specification.key || '');
    const valueText = getPlainSearchText(specification.value || '');

    return uniqueValues([
      label,
      valueText,
      label && valueText ? `${label}: ${valueText}` : '',
    ]);
  });
}

function createSearchDocument({
  id,
  title,
  pageTitle,
  resultType = 'Page',
  section = '',
  navigation,
  content = [],
  keywords = [],
}) {
  const normalizedTitle = getPlainSearchText(title);
  const normalizedPageTitle = getPlainSearchText(pageTitle || title);
  const normalizedSection = getPlainSearchText(section);
  const contentSegments = uniqueValues(flattenTextValues(content));
  const keywordSegments = uniqueValues(flattenTextValues(keywords));
  const snippetSource = uniqueValues([
    normalizedTitle,
    normalizedSection,
    ...contentSegments,
    ...keywordSegments,
  ]).join(' ');

  return {
    id,
    title: normalizedTitle || normalizedPageTitle,
    pageTitle: normalizedPageTitle,
    resultType,
    section: normalizedSection,
    navigation,
    content: contentSegments.join(' '),
    keywords: keywordSegments,
    snippetSource,
    normalized: {
      title: normalizeSearchValue(normalizedTitle),
      pageTitle: normalizeSearchValue(normalizedPageTitle),
      section: normalizeSearchValue(normalizedSection),
      keywords: keywordSegments.map((value) => normalizeSearchValue(value)).filter(Boolean),
      content: normalizeSearchValue(snippetSource),
    },
  };
}

function tokenizeNormalizedValue(value = '') {
  return String(value)
    .split(' ')
    .map((token) => token.trim())
    .filter(Boolean);
}

function hasWholeQueryMatch(normalizedValue, query) {
  const cleanValue = String(normalizedValue || '').trim();
  const cleanQuery = String(query || '').trim();

  if (!cleanValue || !cleanQuery) {
    return false;
  }

  return ` ${cleanValue} `.includes(` ${cleanQuery} `);
}

function startsWithWholeQuery(normalizedValue, query) {
  const cleanValue = String(normalizedValue || '').trim();
  const cleanQuery = String(query || '').trim();

  if (!cleanValue || !cleanQuery) {
    return false;
  }

  return cleanValue === cleanQuery || cleanValue.startsWith(`${cleanQuery} `);
}

function getTokenMatchStats(queryTokens, valueTokens) {
  if (queryTokens.length === 0 || valueTokens.length === 0) {
    return {
      matchedCount: 0,
      exactCount: 0,
      prefixCount: 0,
      matchedAllTokens: false,
    };
  }

  let exactCount = 0;
  let prefixCount = 0;

  queryTokens.forEach((token) => {
    if (valueTokens.includes(token)) {
      exactCount += 1;
      return;
    }

    if (
      token.length >= MIN_PREFIX_MATCH_LENGTH &&
      valueTokens.some((valueToken) => valueToken.startsWith(token))
    ) {
      prefixCount += 1;
    }
  });

  const matchedCount = exactCount + prefixCount;

  return {
    matchedCount,
    exactCount,
    prefixCount,
    matchedAllTokens: matchedCount === queryTokens.length,
  };
}

function scoreField(query, queryTokens, normalizedValue, weights) {
  if (!normalizedValue) {
    return {
      score: 0,
      matchedTokens: 0,
      exactTokenMatches: 0,
      prefixTokenMatches: 0,
      matchedAllTokens: false,
      hasWholeQueryMatch: false,
    };
  }

  const valueTokens = tokenizeNormalizedValue(normalizedValue);
  const tokenMatchStats = getTokenMatchStats(queryTokens, valueTokens);
  const wholeQueryMatch = hasWholeQueryMatch(normalizedValue, query);
  let score = 0;

  if (normalizedValue === query) {
    score = Math.max(score, weights.exact);
  } else if (startsWithWholeQuery(normalizedValue, query)) {
    score = Math.max(score, weights.startsWith);
  } else if (wholeQueryMatch) {
    score = Math.max(score, weights.includes);
  }

  if (tokenMatchStats.matchedCount > 0) {
    const tokenScore =
      tokenMatchStats.exactCount * weights.tokens +
      tokenMatchStats.prefixCount * Math.max(1, weights.tokens - 2);
    score = Math.max(score, tokenScore);

    if (tokenMatchStats.matchedAllTokens) {
      score += weights.tokens * 2;
    }
  }

  return {
    score,
    matchedTokens: tokenMatchStats.matchedCount,
    exactTokenMatches: tokenMatchStats.exactCount,
    prefixTokenMatches: tokenMatchStats.prefixCount,
    matchedAllTokens: tokenMatchStats.matchedAllTokens,
    hasWholeQueryMatch: wholeQueryMatch,
  };
}

function scoreSearchDocument(query, queryTokens, document) {
  const searchFields = [
    { value: document.normalized.title, weights: FIELD_WEIGHTS.title },
    { value: document.normalized.pageTitle, weights: FIELD_WEIGHTS.pageTitle },
    { value: document.normalized.section, weights: FIELD_WEIGHTS.section },
    ...document.normalized.keywords.map((value) => ({
      value,
      weights: FIELD_WEIGHTS.keywords,
    })),
    { value: document.normalized.content, weights: FIELD_WEIGHTS.content },
  ];
  const fieldMatches = searchFields.map(({ value, weights }) =>
    scoreField(query, queryTokens, value, weights)
  );
  const aggregateTokens = tokenizeNormalizedValue(
    uniqueValues([
      document.normalized.title,
      document.normalized.pageTitle,
      document.normalized.section,
      ...document.normalized.keywords,
      document.normalized.content,
    ]).join(' ')
  );
  const aggregateMatch = getTokenMatchStats(queryTokens, aggregateTokens);
  const hasWholeQueryMatchInDocument = fieldMatches.some((fieldMatch) => fieldMatch.hasWholeQueryMatch);
  const isStrongMatch =
    queryTokens.length === 1
      ? hasWholeQueryMatchInDocument ||
        aggregateMatch.exactCount > 0 ||
        aggregateMatch.prefixCount > 0
      : aggregateMatch.matchedAllTokens &&
        (hasWholeQueryMatchInDocument ||
          aggregateMatch.exactCount > 0 ||
          aggregateMatch.prefixCount === queryTokens.length);

  if (!isStrongMatch) {
    return 0;
  }

  let score = 0;
  let matchedFields = 0;

  fieldMatches.forEach((fieldMatch) => {
    if (fieldMatch.score > 0) {
      score += fieldMatch.score;
      matchedFields += 1;
    }
  });

  if (hasWholeQueryMatch(document.normalized.content, query)) {
    score += 18;
  }

  if (hasWholeQueryMatch(document.normalized.title, query)) {
    score += 15;
  }

  if (matchedFields > 1) {
    score += matchedFields * 4;
  }

  if (aggregateMatch.matchedAllTokens) {
    score += 8;
  }

  return score;
}

function buildSearchSnippet(query, snippetSource) {
  const trimmedQuery = String(query || '').trim();
  const plainSource = getPlainSearchText(snippetSource).replace(/\s+/g, ' ').trim();

  if (!plainSource) {
    return '';
  }

  if (!trimmedQuery) {
    return getRichTextExcerpt(plainSource, 180);
  }

  const normalizedSource = plainSource.toLowerCase();
  const queryTokens = trimmedQuery.toLowerCase().split(/\s+/).filter(Boolean);
  let matchIndex = normalizedSource.indexOf(trimmedQuery.toLowerCase());

  if (matchIndex === -1) {
    matchIndex = queryTokens.reduce((foundIndex, token) => {
      if (foundIndex !== -1) {
        return foundIndex;
      }

      return normalizedSource.indexOf(token);
    }, -1);
  }

  if (matchIndex === -1) {
    return getRichTextExcerpt(plainSource, 180);
  }

  const snippetStart = Math.max(0, matchIndex - 70);
  const snippetEnd = Math.min(
    plainSource.length,
    matchIndex + Math.max(trimmedQuery.length, 30) + 90
  );
  const prefix = snippetStart > 0 ? '...' : '';
  const suffix = snippetEnd < plainSource.length ? '...' : '';

  return `${prefix}${plainSource.slice(snippetStart, snippetEnd).trim()}${suffix}`;
}

function buildHomeDocument(siteContent) {
  const whyChooseItems =
    Array.isArray(siteContent?.why_choose_items) && siteContent.why_choose_items.length > 0
      ? siteContent.why_choose_items
      : DEFAULT_WHY_CHOOSE_ITEMS;
  const industryItems =
    Array.isArray(siteContent?.industries_serve_items) && siteContent.industries_serve_items.length > 0
      ? siteContent.industries_serve_items.map((item) => item?.name)
      : DEFAULT_INDUSTRIES;

  return createSearchDocument({
    id: 'page-home',
    title: 'Home',
    pageTitle: 'Home',
    section: 'Homepage',
    navigation: { pathname: '/' },
    content: [
      collectSiteContentByKey(siteContent, [
        'hero_',
        'mission_',
        'vision_',
        'why_choose_',
        'industries_serve_',
        'home_product_',
      ]),
      siteContent?.hero_intro_title,
      siteContent?.hero_intro_text,
      siteContent?.mission_short || DEFAULT_MISSION_SHORT,
      siteContent?.mission_full || DEFAULT_MISSION_FULL,
      siteContent?.vision_short || DEFAULT_VISION_SHORT,
      siteContent?.vision_full || DEFAULT_VISION_FULL,
      siteContent?.why_choose_title || DEFAULT_WHY_CHOOSE_TITLE,
      siteContent?.why_choose_highlight || DEFAULT_WHY_CHOOSE_HIGHLIGHT,
      siteContent?.why_choose_intro || DEFAULT_WHY_CHOOSE_INTRO,
      whyChooseItems,
      siteContent?.industries_serve_title || DEFAULT_INDUSTRIES_TITLE,
      siteContent?.industries_serve_intro || DEFAULT_INDUSTRIES_INTRO,
      industryItems,
    ],
    keywords: [
      'home',
      'homepage',
      'landing page',
      'Hari Krishna Industries',
      'industrial valves',
      'instrumentation solutions',
    ],
  });
}

function buildProductsDocument(siteContent) {
  const categoryTerms = buildCategoryTerms(siteContent);

  return createSearchDocument({
    id: 'page-products',
    title: 'Products',
    pageTitle: 'Products',
    section: 'Product Categories',
    navigation: { pathname: '/products' },
    content: [
      'Product categories',
      'All products',
      'Browse products by category or subcategory',
      categoryTerms,
      collectSiteContentByKey(siteContent, ['product_dropdown_', 'product_category_']),
    ],
    keywords: [
      'products',
      'product page',
      'product listing',
      'category',
      'subcategory',
      ...categoryTerms,
    ],
  });
}

function buildCategoryDocuments(siteContent) {
  return buildCategoryRecords(siteContent).map((category) =>
    createSearchDocument({
      id: `category-${normalizeSearchValue(category.label)}`,
      title: category.label,
      pageTitle: 'Products',
      section: category.section,
      navigation: {
        pathname: '/products',
        search: `?category=${encodeURIComponent(category.label)}`,
      },
      content: [
        category.label,
        'Browse products in this category',
        'Filtered products page',
      ],
      keywords: ['products', 'category', 'subcategory', category.section, category.label],
    })
  );
}

function buildAboutDocument(siteContent) {
  const aboutWhyItems =
    Array.isArray(siteContent?.about_why_items) && siteContent.about_why_items.length > 0
      ? siteContent.about_why_items
      : DEFAULT_ABOUT_WHY;

  return createSearchDocument({
    id: 'page-about',
    title: siteContent?.about_hero_title || 'About HKI',
    pageTitle: 'About Us',
    section: 'Company Overview',
    navigation: { pathname: '/about' },
    content: [
      collectSiteContentByKey(siteContent, ['about_']),
      siteContent?.about_hero_subtitle,
      siteContent?.about_overview_heading,
      siteContent?.about_overview_text,
      DEFAULT_ABOUT_PARAGRAPHS,
      siteContent?.about_why_title || 'Why Choose HKI?',
      aboutWhyItems,
      siteContent?.about_stats,
    ],
    keywords: [
      'about',
      'about us',
      'company profile',
      'precision engineering',
      'Hari Krishna Industries',
    ],
  });
}

function buildCertificatesDocument(certificates = []) {
  return createSearchDocument({
    id: 'page-certificates',
    title: 'Certificates',
    pageTitle: 'Certificates',
    section: 'Quality Assurance',
    navigation: { pathname: '/certificates' },
    content: [
      'Certificates',
      'Quality assurance',
      'Industrial certifications',
      certificates.map((certificate) => certificate?.name),
    ],
    keywords: ['certificates', 'certificate', 'quality', 'quality assurance', 'certification'],
  });
}

function buildCatalogDocument(catalogs = []) {
  return createSearchDocument({
    id: 'page-catalog',
    title: 'Download Catalogue',
    pageTitle: 'Catalog',
    section: 'Catalogues',
    navigation: { pathname: '/catalog' },
    content: [
      'Download catalogue',
      'Catalogues',
      'Product catalogues',
      catalogs.map((catalog) => catalog?.title),
    ],
    keywords: ['catalog', 'catalogue', 'download catalogue', 'download catalog', 'brochure'],
  });
}

function buildNewsListingDocument() {
  return createSearchDocument({
    id: 'page-news-blogs',
    title: 'News & Blogs',
    pageTitle: 'News & Blogs',
    section: 'Updates',
    navigation: { pathname: '/news-blogs' },
    content: [
      'News and blogs',
      'Latest updates',
      'Industrial insights',
      'Manufacturing news',
    ],
    keywords: ['news', 'blogs', 'updates', 'articles'],
  });
}

function buildContactDocuments(siteContent) {
  const footerContactTexts = uniqueValues([
    siteContent?.footer_company_name,
    siteContent?.footer_phone,
    siteContent?.footer_email,
    siteContent?.footer_address,
    siteContent?.footer_contact_heading,
    siteContent?.footer_map_label,
  ]);

  return [
    createSearchDocument({
      id: 'page-contact',
      title: 'Contact Us',
      pageTitle: 'Contact Us',
      section: 'Contact Page',
      navigation: { pathname: '/contact' },
      content: [
        footerContactTexts,
        'Contact us',
        'Meet us',
        'Customer enquiry form',
        DEFAULT_CONTACT_PHONE,
        DEFAULT_CONTACT_EMAIL,
        DEFAULT_CONTACT_ADDRESS,
      ],
      keywords: ['contact', 'contact us', 'meet us', 'phone', 'email', 'address'],
    }),
    createSearchDocument({
      id: 'page-contact-meet-us',
      title: 'Meet Us',
      pageTitle: 'Contact Us',
      section: 'Meet Us',
      navigation: { pathname: '/contact', hash: '#meet-us' },
      content: [
        'Meet us',
        'Office address',
        DEFAULT_CONTACT_PHONE,
        DEFAULT_CONTACT_EMAIL,
        DEFAULT_CONTACT_ADDRESS,
        footerContactTexts,
      ],
      keywords: ['meet us', 'address', 'location', 'phone', 'email'],
    }),
    createSearchDocument({
      id: 'page-contact-map',
      title: 'Map',
      pageTitle: 'Contact Us',
      section: 'Map',
      navigation: { pathname: '/contact', hash: '#map-section' },
      content: ['Map', 'Google map', 'Directions', 'Visit office', DEFAULT_CONTACT_ADDRESS],
      keywords: ['map', 'directions', 'office map', 'location'],
    }),
    createSearchDocument({
      id: 'page-contact-inquiry-form',
      title: 'Customer Enquiry Form',
      pageTitle: 'Contact Us',
      section: 'Inquiry Form',
      navigation: { pathname: '/contact', hash: '#inquiry-form' },
      content: [
        'Customer enquiry form',
        'Name',
        'Email',
        'Phone',
        'Message',
        'Send',
        "Thank you! Your message has been sent successfully. We'll get back to you soon.",
      ],
      keywords: ['enquiry form', 'inquiry form', 'contact form', 'quote request'],
    }),
  ];
}

function buildInquiryDocument() {
  return createSearchDocument({
    id: 'page-inquiry',
    title: 'Inquiry',
    pageTitle: 'Inquiry',
    section: 'Inquiry Support',
    navigation: { pathname: '/inquiry' },
    content: [
      'Inquiry support',
      'Contact our team directly',
      'Open contact form',
      'View contact details',
      'Quick quote requests',
      'Product information',
      'Technical support',
      'Custom solutions',
      'Pricing inquiries',
      'Direct contact',
    ],
    keywords: ['inquiry', 'quote', 'technical support', 'pricing inquiries'],
  });
}

function buildProductDocuments(products = []) {
  return products
    .map((product) => {
      const productId = product?._id ?? product?.id;
      const productName = getPlainSearchText(product?.name || product?.title || '');
      const productPath = buildProductPath(product);

      if (!productId || !productName) {
        return null;
      }

      const category = getPlainSearchText(product?.category || '');
      const subcategory = getPlainSearchText(product?.subcategory || '');

      return createSearchDocument({
        id: `product-${productId}`,
        title: productName,
        pageTitle: 'Products',
        resultType: 'Product',
        section: uniqueValues([subcategory, category]).join(' / '),
        navigation: { pathname: productPath },
        content: [
          productName,
          category,
          subcategory,
          product?.description,
          normalizeProductFeatures(product?.features),
          normalizeProductSpecifications(product?.specifications),
        ],
        keywords: ['product', 'download catalog', 'product inquiry', category, subcategory],
      });
    })
    .filter(Boolean);
}

function buildNewsDocuments(newsItems = []) {
  return newsItems
    .map((item) => {
      const id = item?.id ?? item?._id;
      const title = getPlainSearchText(item?.title || '');

      if (!id || !title) {
        return null;
      }

      return createSearchDocument({
        id: `news-${id}`,
        title,
        pageTitle: 'News & Blogs',
        resultType: 'News',
        section: getPlainSearchText(item?.date || ''),
        navigation: { pathname: `/news-blogs/${id}` },
        content: [item?.title, item?.description, item?.content, item?.shortDescription, item?.date],
        keywords: ['news', 'blog', 'article', 'updates'],
      });
    })
    .filter(Boolean);
}

export function normalizeSearchWord(word = '') {
  if (word.length <= 3) {
    return word;
  }

  if (word.endsWith('ies')) {
    return `${word.slice(0, -3)}y`;
  }

  if (word.endsWith('ves')) {
    return word.slice(0, -1);
  }

  if (word.endsWith('s') && !word.endsWith('ss')) {
    return word.slice(0, -1);
  }

  return word;
}

export function normalizeSearchValue(value = '') {
  return String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(normalizeSearchWord)
    .join(' ');
}

export function getSearchTokens(value = '') {
  return normalizeSearchValue(value)
    .split(' ')
    .filter(Boolean);
}

export function getPlainSearchText(value = '') {
  return getRichTextPlainText(value).replace(/\s+/g, ' ').trim();
}

export function buildSiteSearchDocuments({
  siteContent,
  products = [],
  newsItems = [],
  certificates = [],
  catalogs = [],
} = {}) {
  return [
    buildHomeDocument(siteContent),
    buildProductsDocument(siteContent),
    ...buildCategoryDocuments(siteContent),
    buildAboutDocument(siteContent),
    ...buildContactDocuments(siteContent),
    buildCertificatesDocument(certificates),
    buildCatalogDocument(catalogs),
    buildNewsListingDocument(),
    buildInquiryDocument(),
    ...buildProductDocuments(products),
    ...buildNewsDocuments(newsItems),
  ];
}

export function searchSiteDocuments(query, documents = [], options = {}) {
  const trimmedQuery = String(query || '').trim();
  const normalizedQuery = normalizeSearchValue(trimmedQuery);
  const queryTokens = getSearchTokens(trimmedQuery);
  const limit = Number.isFinite(options.limit) ? options.limit : 50;

  if (!trimmedQuery || !normalizedQuery || queryTokens.length === 0) {
    return [];
  }

  return documents
    .map((document) => {
      const score = scoreSearchDocument(normalizedQuery, queryTokens, document);

      if (score <= 0) {
        return null;
      }

      return {
        ...document,
        score,
        snippet: buildSearchSnippet(trimmedQuery, document.snippetSource || document.content),
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return (
        (SEARCH_RESULT_TYPE_ORDER[left.resultType] ?? 99) -
        (SEARCH_RESULT_TYPE_ORDER[right.resultType] ?? 99)
      );
    })
    .slice(0, limit);
}

export function buildSearchResultsNavigation(query) {
  return {
    pathname: '/search',
    search: `?q=${encodeURIComponent(String(query || '').trim())}`,
  };
}

export function getSearchResultMeta(result) {
  return uniqueValues([result.resultType, result.pageTitle, result.section]).join(' - ');
}
